import { createContext } from 'react';

export type EnvVars = { [k: string]: string };

const AEP_PREFIX = 'REACT_APP_';
const INGRAM_PREFIX = 'REACT_APP_INGRAM__';

const buildEnvironments = [
  'development',
  'production',
  'staging',
  'test',
] as const;
type BuildEnvironment = typeof buildEnvironments[number];

type OptionalKey =
  | 'DEV_PROXY_HOSTNAME'
  | 'PRICING_API_BASE'
  | 'DISCOUNT_API_BASE'
  | 'VMF_API_BASE'
  | 'VP_API_BASE'
  | 'EU_BASE'
  | 'GPE__API_BASE'
  | 'USER_MT'
  | 'BIDS_API_BASE';
const requiredKeys = ['API_BASE', 'MSAL_CLIENT_ID', 'MSAL_TENANT_ID'] as const;
type RequiredKey = typeof requiredKeys[number];

type RawBuildConfig = {
  buildEnvironment: BuildEnvironment;
} & { [rk in RequiredKey]: string } & Partial<{
    [ok in OptionalKey]: string;
  }> & { [k: string]: string };

export type BuildConfig = {
  vars: Readonly<RawBuildConfig>;
};

export const buildConfigContext = createContext<BuildConfig | undefined>(
  undefined
);

export function createBuildConfig(envVars: EnvVars): BuildConfig {
  const config: Partial<RawBuildConfig> = {};

  Object.entries(envVars).forEach(([key, value]) => {
    if (key.startsWith(INGRAM_PREFIX)) {
      config[key.substring(INGRAM_PREFIX.length)] = value;
    } else if (key.startsWith(AEP_PREFIX)) {
      config[key.substring(AEP_PREFIX.length)] = value;
    } else if (key === 'NODE_ENV') {
      config.buildEnvironment = value as BuildEnvironment;
    } else if (envVars['NODE_ENV'] === 'development') {
      console.warn(`[BuildConfig] Env var "${key}" skipped.`);
    }
  });

  // Validate
  const buildEnvironment = config.buildEnvironment as BuildEnvironment;
  if (!buildEnvironments.includes(buildEnvironment)) {
    throw new Error(
      `[BuildConfig] invalid NODE_ENV=${buildEnvironment}; Must be one of: ${JSON.stringify(
        buildEnvironments
      )}.`
    );
  }

  requiredKeys.forEach((key) => {
    if (!config[key]) {
      throw new Error(`[BuildConfig] ${key} not set.`);
    }
  });

  return {
    // Freeze to prevent modifications.
    vars: Object.freeze(config as RawBuildConfig),
  };
}
